import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter } from 'rxjs';
import { MatButton } from '@angular/material/button';
import { AnalyticsEvent } from '@gains-com/shared';
import { ButtonLoadingDirective, IconComponent } from '@gains-com/ui-angular';
import { Analytics } from '../../core/analytics/analytics.service.js';
import { DeviceModeViewService } from '../../core/view-states/device-mode-view.service.js';
import { LocationGateState, LocationGateViewService } from '../../core/view-states/location-gate-view.service.js';
import { UnverifiedDetailByReason } from '../../components/location/location-gate-copy.js';

interface LocationBannerCopy {
  message: string;
  cta: string;
}

const SHARE_MESSAGE = 'Sweeps coins play, purchases, and Daily Claims are unavailable until you share your location.';

const CopyByState: Record<Exclude<LocationGateState, 'verified'>, LocationBannerCopy> = {
  prompt: { message: SHARE_MESSAGE, cta: 'Enable location' },
  denied: { message: SHARE_MESSAGE, cta: 'Enable location' },
  denied_forever: {
    message:
      'Sweeps coins play, purchases, and Daily Claims are unavailable until you enable location access in your browser settings.',
    cta: 'Show me how'
  },
  unverified: { message: "We couldn't verify your location.", cta: 'Retry verification' }
};

/**
 * Non-dismissable location banner (device-location v4.1 §10). Shell-level,
 * directly below the top toolbar. Fails closed until the gate state resolves
 * in-browser (SSR/hydration render nothing). Suppressed while the
 * region-restricted notice shows, and while a soft prompt is pending or open
 * for this app-open.
 */
@Component({
  selector: 'app-location-banner',
  templateUrl: './location-banner.component.html',
  styleUrl: './location-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonLoadingDirective, IconComponent, MatButton]
})
export class LocationBannerComponent {
  private readonly _deviceModeView = inject(DeviceModeViewService);
  private readonly _analytics = inject(Analytics);
  protected readonly gate = inject(LocationGateViewService);

  protected readonly verifying = signal(false);

  readonly shouldShowBanner = computed(() => {
    const state = this.gate.state();
    if (!state || state === 'verified') {
      return false;
    }
    // Priority: region-restricted notice > location banner (one at a time).
    if (this._deviceModeView.regionRestricted()) {
      return false;
    }
    return !this.gate.softPromptPending();
  });

  protected readonly copy = computed<LocationBannerCopy | null>(() => {
    const state = this.gate.state();
    if (!state || state === 'verified') {
      return null;
    }

    const copy = CopyByState[state];
    if (state !== 'unverified') {
      return copy;
    }

    const reason = this._deviceModeView.lastAttestResult();
    const detail = reason && reason !== 'verified' ? UnverifiedDetailByReason[reason] : undefined;
    return detail ? { ...copy, message: `${copy.message} ${detail}` } : copy;
  });

  constructor() {
    toObservable(computed(() => (this.shouldShowBanner() ? this.gate.state() : null)))
      .pipe(
        distinctUntilChanged(),
        filter((state): state is LocationGateState => !!state),
        takeUntilDestroyed()
      )
      .subscribe((state) => this._analytics.trackEvent(AnalyticsEvent.LocationBannerShown, { state }));
  }

  protected async onCtaClick() {
    const state = this.gate.state();
    if (!state || state === 'verified' || this.verifying()) {
      return;
    }

    this._analytics.trackEvent(AnalyticsEvent.LocationBannerCtaClicked, { state });
    this.verifying.set(true);
    try {
      switch (state) {
        case 'prompt':
        case 'unverified':
          await this.gate.attemptVerification('banner');
          break;
        case 'denied':
          await this.gate.retryFromDenied('banner');
          break;
        case 'denied_forever':
          await this.gate.openInstructionsDialog();
          break;
      }
    } finally {
      this.verifying.set(false);
    }
  }
}
